import { useEffect, useState } from 'react'

type Theme = 'light' | 'dark' | 'system'

/**
 * Hook for managing theme (light/dark mode).
 * Persists user preference in localStorage.
 * 
 * Frontend defaults to system preference (typically dark for dev environments).
 * 
 * @example
 * const { theme, setTheme, resolvedTheme } = useTheme()
 * 
 * // Toggle
 * <Button onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}>
 *   {resolvedTheme === 'dark' ? <Sun /> : <Moon />}
 * </Button>
 */
export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === 'undefined') return 'system'
    return (localStorage.getItem('theme') as Theme) || 'system'
  })

  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>('light')

  useEffect(() => {
    const root = document.documentElement

    const applyTheme = (newTheme: Theme) => {
      let resolved: 'light' | 'dark'
      
      if (newTheme === 'system') {
        resolved = window.matchMedia('(prefers-color-scheme: dark)').matches 
          ? 'dark' 
          : 'light'
      } else {
        resolved = newTheme
      }

      setResolvedTheme(resolved)

      // Apply the theme
      root.classList.remove('light', 'dark')
      root.classList.add(resolved)
    }

    applyTheme(theme)

    // Listen for system preference changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = () => {
      if (theme === 'system') {
        applyTheme('system')
      }
    }
    mediaQuery.addEventListener('change', handleChange)
    
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [theme])

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme)
    localStorage.setItem('theme', newTheme)
  }

  return { theme, setTheme, resolvedTheme }
}
